import type { Action, Subject } from '@ftthnms/shared';
import { Button, EmptyState } from '@ftthnms/ui';
import { ChevronLeft, ChevronRight, ShieldX } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, nf } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * Modal form on the native <dialog>. `onSubmit` may throw an ApiError: field errors go to
 * `children(fieldErrors)`, other messages are shown above the buttons. Resolving closes the dialog.
 */
export function FormDialog({
  title,
  submitLabel = 'Simpan',
  width = 460,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  submitLabel?: string;
  width?: number;
  onSubmit: () => Promise<void>;
  onClose: () => void;
  children: (fieldErrors: Record<string, string>) => ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onSubmit();
      ref.current?.close();
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, undefined, (err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  const fe = error?.fieldErrors ?? {};
  return (
    <dialog ref={ref} className="fn-dialog" onClose={onClose} aria-label={title}>
      <form
        className="fn-panel"
        style={{
          background: 'var(--bg-surface)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          width,
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h2 className="fn-card__title" style={{ fontSize: 15 }}>
          {title}
        </h2>
        {children(fe)}
        {error && Object.keys(fe).length === 0 && (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{error.message}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" type="button" onClick={() => ref.current?.close()} disabled={busy}>
            Batal
          </Button>
          <Button variant="primary" type="submit" loading={busy}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

/** Table footer: total and page navigation. */
export function Pager({
  total,
  unit,
  page,
  pageSize,
  onPage,
  note,
}: {
  total: number;
  unit: string;
  page: number;
  pageSize: number;
  onPage: (p: number) => void;
  note?: ReactNode;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="fn-table-footer">
      <div>
        <strong>{nf.format(total)}</strong> {unit}
        {note}
      </div>
      <nav className="fn-pagination" aria-label="Halaman">
        <button
          type="button"
          className="fn-page"
          disabled={page <= 1}
          aria-label="Sebelumnya"
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft className="i i--sm" aria-hidden />
        </button>
        <span className="fn-page" aria-current="page">
          {page}
        </span>
        <span className="fn-hint">dari {lastPage}</span>
        <button
          type="button"
          className="fn-page"
          disabled={page >= lastPage}
          aria-label="Selanjutnya"
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight className="i i--sm" aria-hidden />
        </button>
      </nav>
    </div>
  );
}

export function Switch({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <span className="fn-switch" title={label}>
      <input
        className="fn-switch__input"
        type="checkbox"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="fn-switch__track" />
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  wide,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  wide?: boolean;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`fn-field${wide ? ' span-2' : ''}`}>
      <span className="fn-label">
        {label}
        {required && <span className="req"> *</span>}
      </span>
      {children}
      {error ? (
        <span className="fn-hint fn-hint--error">{error}</span>
      ) : (
        hint && <span className="fn-hint">{hint}</span>
      )}
    </label>
  );
}

/** Renders the page only when the ability allows it (the API checks again). */
export function RequireAbility({
  action,
  subject,
  children,
}: {
  action: Action;
  subject: Subject;
  children: ReactNode;
}) {
  const { ability } = useSession();
  if (ability.can(action, subject)) return <>{children}</>;
  return (
    <div className="fn-page-body">
      <EmptyState
        icon={<ShieldX className="i i--xl" aria-hidden />}
        title="Tidak punya akses"
        text="Halaman ini tidak tersedia untuk peran Anda. Hubungi Admin bila Anda memerlukannya."
      />
    </div>
  );
}

/** Segmented tabs (design system fn-tabs). */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: ReadonlyArray<readonly [T, ReactNode]>;
  value: T;
  onChange: (t: T) => void;
}) {
  return (
    <div className="fn-tabs" role="tablist">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          className="fn-tab"
          aria-selected={value === key}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
